import {useTranslation} from "react-i18next";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {useForm} from "react-hook-form";
import {Grid} from "@mui/material";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage} from "../../utils/resourceUtils";
import {Dialog} from "../base/Dialog";
import {LabeledInput} from "../base/LabeledInput";
import {CompanyMemberRole} from "../../types/domain/companyMember";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {cancelUserInvitation, createUserInvitation, patchUserInvitation} from "../../services/invitationService";
import {TextInput} from "../base/TextInput";
import {SelectInput} from "../base/SelectInput";
import {UserInvitation} from "../../types/domain/userInvitation";
import {UserIcon} from "../icons/UserIcon";
import {TFunction} from "i18next";

export interface Props {
    mode: "create" | "edit";
    companyId: string;
    invitation?: UserInvitation;
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const CompanyMemberInviteDialog: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const {companyId, open, onClose, mode, invitation} = props;
    const invitationManagement = useResourceManagement({
        createUserInvitation,
        patchUserInvitation,
        cancelUserInvitation,
    });
    const initFormValues = mode === "edit" ? invitationToFormInputs(invitation!) : DEFAULT_FORM_INPUTS;

    const MEMBER_INVITE_SCHEMA = z.object({
        email: z
            .string()
            .email()
            .refine((email) => email === email.toLowerCase(), {
                message: "Email can only contain lowercase characters",
            }),
        role: z.nativeEnum(CompanyMemberRole),
    });

    const {
        control,
        handleSubmit,
        register,
        formState: {errors},
    } = useForm({
        defaultValues: initFormValues,
        resolver: zodResolver(MEMBER_INVITE_SCHEMA),
    });

    const onSubmit = (inputs: FormInputs) => {
        invitationManagement.reset();

        if (mode === "create") {
            invitationManagement
                .createUserInvitation({companyId, userEmail: inputs.email, role: inputs.role})
                .then(() => onClose(true))
                .catch(swallowException);
        } else {
            invitationManagement
                .patchUserInvitation(invitation!.id, {
                    role: inputs.role,
                })
                .then(() => onClose(true))
                .catch(swallowException);
        }
    };

    const onDelete = () => {
        invitationManagement.reset();

        invitationManagement
            .cancelUserInvitation(invitation!.id)
            .then(() => onClose(true))
            .catch(swallowException);
    };

    return (
        <Dialog
            width="50rem"
            enableSubmit
            enableDelete={mode === "edit"}
            deleteTitle={t("dialogs.member_invite.cancel_invitation")}
            submitTitle={t("dialogs.actions.submit")}
            title={mode === "edit" ? t("dialogs.member_invite.edit_title") : t("dialogs.member_invite.invite_title")}
            titleIcon={
                <UserIcon
                    sx={{
                        stroke: "#404DB8",
                        width: "1.125rem",
                        height: "1.125rem",
                    }}
                />
            }
            open={open}
            error={getResourceErrorMessage(invitationManagement.resource)}
            deleteConfirmation={t("dialogs.member_invite.delete_confirmation")}
            onClose={() => onClose(false)}
            onDelete={onDelete}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(onSubmit)}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <LabeledInput label={t("dialogs.member_invite.email")}>
                                <TextInput
                                    name="email"
                                    type="text"
                                    register={register}
                                    errors={errors}
                                    disabled={mode === "edit"}
                                />
                            </LabeledInput>
                        </Grid>
                        <Grid item xs={12}>
                            <LabeledInput label={t("dialogs.member_invite.role")}>
                                <SelectInput
                                    name="role"
                                    control={control}
                                    options={getRoleOptions(t)}
                                    initialValue={DEFAULT_FORM_INPUTS.role}
                                    getOptionLabel={(option) => option.label}
                                />
                            </LabeledInput>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
};

const getRoleOptions = (t: TFunction) =>
    Object.values(CompanyMemberRole)
        .filter((role) => role !== CompanyMemberRole.MANAGER)
        .map((role) => ({
            id: role,
            label: t(`enumerations.company_roles.${role.toLowerCase()}`),
        }));

interface FormInputs {
    email: string;
    role: CompanyMemberRole;
}

const invitationToFormInputs = (invitation: UserInvitation): FormInputs => ({
    email: invitation.userEmail,
    role: invitation.role,
});

const DEFAULT_FORM_INPUTS: FormInputs = {
    email: "",
    role: CompanyMemberRole.VIEWER,
};
